import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Alert, Button, Form } from 'react-bootstrap';
import { useAuth } from "react-oidc-context";
import { useUser } from "../context/UserContext";
import { hostMonitoringAPI } from '../utils/apiClient';
import DataDropdown from '../components/DataDropdown';
import HostsTable from '../components/HostsTable';
import LoadingSpinner from '../components/LoadingSpinner';
import '../components/Dropdowns.css';

function HostsMonitoring() {
  useEffect(() => {
    document.title = 'Host Monitoring - LostLogic';
  }, []);
  const auth = useAuth();
  const { orgHash } = useUser();
  
  // Data states
  const [hosts, setHosts] = useState([]);
  const [filteredHosts, setFilteredHosts] = useState([]);
  const [knownHostsMap, setKnownHostsMap] = useState({});
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNotConfigured, setIsNotConfigured] = useState(false);
  
  // Filter states
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDomains, setAvailableDomains] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  
  // UI states
  const [updatingHosts, setUpdatingHosts] = useState(new Set());
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Check if we have a cached "not configured" state (valid for 24 hours)
  const checkNotConfiguredCache = useCallback(() => {
    if (!orgHash) return false;
    
    const cacheKey = `hosts_monitoring_not_configured_${orgHash}`;
    const cached = localStorage.getItem(cacheKey);
    
    if (cached) {
      const { timestamp } = JSON.parse(cached);
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000;
      
      if (now - timestamp < twentyFourHours) {
        console.log('Hosts monitoring not configured (cached)');
        return true;
      } else {
        localStorage.removeItem(cacheKey);
      }
    }
    return false;
  }, [orgHash]);

  // Set "not configured" state in cache (24 hour expiry)
  const setNotConfiguredCache = useCallback(() => {
    if (!orgHash) return;
    
    const cacheKey = `hosts_monitoring_not_configured_${orgHash}`;
    localStorage.setItem(cacheKey, JSON.stringify({
      timestamp: Date.now()
    }));
  }, [orgHash]);

  /**
   * Fetch available dates
   */
  const fetchAvailableDates = useCallback(async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) {
      console.log('Skipping fetchAvailableDates - waiting for auth');
      return [];
    }

    try {
      console.log('Fetching available dates...');
      const data = await hostMonitoringAPI.getDates(token, orgHash);
      console.log('Available dates:', data);

      setAvailableDates(data.dates || []);
      
      if (data.most_recent && !selectedDate) {
        setSelectedDate(data.most_recent);
      }

      return data.dates;
    } catch (err) {
      console.error('Error fetching dates:', err);
      setError('Failed to load available dates');
      return [];
    }
  }, [auth.user, orgHash, selectedDate]);

  /**
   * Fetch known hosts status
   */
  const fetchKnownHosts = useCallback(async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) {
      console.log('Skipping fetchKnownHosts - waiting for auth');
      return;
    }

    try {
      console.log('Fetching known hosts status...');
      const data = await hostMonitoringAPI.getKnownHosts(token, orgHash);
      console.log('Known hosts:', data);
      setKnownHostsMap(data.hosts || {});
    } catch (err) {
      console.error('Error fetching known hosts:', err);
      setKnownHostsMap({});
    }
  }, [auth.user, orgHash]);

  /**
   * Fetch host monitoring data
   */
  const fetchHostData = useCallback(async (date) => {
    if (!date) {
      console.log('No date selected, skipping data fetch');
      return;
    }

    const token = auth.user?.access_token;
    if (!token || !orgHash) {
      console.log('Skipping fetchHostData - waiting for auth');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`Fetching host data for date: ${date}`);
      const result = await hostMonitoringAPI.getHostData(token, orgHash, date);
      
      const hostData = result.data;
      console.log(`Loaded ${hostData.length} hosts from S3`);

      if (!hostData || hostData.length === 0) {
        console.log('Empty host data received - marking as not configured');
        setNotConfiguredCache();
        setIsNotConfigured(true);
        setHosts([]);
        setLoading(false);
        return;
      }

      // Map data to expected format
      const mappedData = hostData.map(item => ({
        host: item.hosts || item.host,
        domain: item.domain || '',
        discovered: item.discovered || ''
      }));

      setHosts(mappedData);

      // Extract unique domains
      const domains = [...new Set(mappedData.map(h => h.domain).filter(d => d))];
      setAvailableDomains(domains.sort());

      setLoading(false);
    } catch (err) {
      console.error('Error fetching host data:', err);
      setError(err.message || 'Failed to load host data');
      setHosts([]);
      setLoading(false);
    }
  }, [auth.user, orgHash, setNotConfiguredCache]);

  /**
   * Toggle known status for a host
   */
  const toggleKnownStatus = async (host, domain, currentStatus) => {
    const token = auth.user?.access_token;
    if (!token) return;

    // Optimistic update
    setUpdatingHosts(prev => new Set(prev).add(host));
    const newStatus = !currentStatus;
    
    setKnownHostsMap(prev => ({
      ...prev,
      [host]: newStatus
    }));

    try {
      console.log(`Toggling known status for ${host} to ${newStatus}`);
      await hostMonitoringAPI.toggleKnown(token, orgHash, host, domain, newStatus);
      console.log('Status updated successfully');
    } catch (err) {
      console.error('Error toggling known status:', err);
      
      // Revert on error
      setKnownHostsMap(prev => ({
        ...prev,
        [host]: currentStatus
      }));
      
      alert(`Failed to update known status: ${err.message}`);
    } finally {
      setUpdatingHosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(host);
        return newSet;
      });
    }
  };

  /**
   * Manual refresh
   */
  const handleRefresh = () => {
    if (selectedDate) {
      fetchHostData(selectedDate);
      fetchKnownHosts();
    }
  };

  /**
   * Initial data load
   */
  useEffect(() => {
    const loadInitialData = async () => {
      const token = auth.user?.access_token;
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      if (!orgHash) {
        console.log('Waiting for orgHash...');
        return;
      }

      if (checkNotConfiguredCache()) {
        setIsNotConfigured(true);
        setLoading(false);
        return;
      }

      const dates = await fetchAvailableDates();
      await fetchKnownHosts();
      
      if (!dates || dates.length === 0) {
        setNotConfiguredCache();
        setIsNotConfigured(true);
        setLoading(false);
      }
    };

    loadInitialData();
  }, [auth.user, orgHash, fetchAvailableDates, fetchKnownHosts, checkNotConfiguredCache, setNotConfiguredCache]);

  /**
   * Load host data when date changes
   */
  useEffect(() => {
    if (selectedDate) {
      fetchHostData(selectedDate);
    }
  }, [selectedDate, fetchHostData]);

  /**
   * Filter hosts by domain
   */
  useEffect(() => {
    if (selectedDomain === 'all') {
      setFilteredHosts(hosts);
    } else {
      setFilteredHosts(hosts.filter(h => h.domain === selectedDomain));
    }
  }, [hosts, selectedDomain]);

  // Not configured view
  if (isNotConfigured) {
    return (
      <Container fluid className="mt-4">
        <h2>Host Monitoring</h2>
        <Alert variant="info" className="mt-4">
          <Alert.Heading>
            <i className="fas fa-info-circle me-2"></i>
            Hosts Monitoring is not configured
          </Alert.Heading>
          <p className="mb-0">
            This feature is not yet available for your organization. Please contact your administrator for more information.
          </p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <h2>Host Monitoring</h2>
        </Col>
        <Col xs="auto">
          <Button 
            variant="outline-primary" 
            onClick={handleRefresh}
            disabled={loading}
          >
            <i className="fas fa-sync-alt"></i> Refresh
          </Button>
          {' '}
          <Button 
            variant={autoRefresh ? "success" : "outline-secondary"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            size="sm"
          >
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </Button>
        </Col>
      </Row>

      {/* Error Display */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Row className="mb-3">
        <Col xs={12} lg={10}>
          <Form className="d-flex flex-wrap align-items-center">
            <DataDropdown 
              setSelectedProperty={(value) => {
                if (value === 'All Domains') {
                  setSelectedDomain('all');
                } else {
                  setSelectedDomain(value);
                }
              }}
              data={['All Domains', ...availableDomains]}
              placeholder="Domain" 
              inputID="domainSelector" 
              inputName="domainProperties" 
              className="m-1" 
              zIndex="10"
              value={selectedDomain === 'all' ? 'All Domains' : selectedDomain}
              disabled={loading}
            />
            {selectedDomain !== 'all' && filteredHosts.length > 0 && (
              <small className="text-muted ms-2">
                ({filteredHosts.length} hosts)
              </small>
            )}
          </Form>
        </Col>
        <Col xs={12} lg={2} className="d-flex justify-content-lg-end align-items-center">
          <DataDropdown 
            setSelectedProperty={setSelectedDate}
            data={availableDates}
            placeholder={availableDates.length === 0 ? "No dates available" : "Date"}
            inputID="dateSelector" 
            inputName="dateProperties"
            className="m-1"
            zIndex="6"
            value={selectedDate}
            disabled={loading}
          />
        </Col>
      </Row>

      {/* Host Table Section */}
      <Row>
        <Col>
          <h4 className="mb-3">Discovered Hosts</h4>
          
          {/* Show spinner inline when loading */}
          {loading && (
            <Row className="mt-5">
              <Col className="text-center">
                <LoadingSpinner message="Loading hosts data..." />
              </Col>
            </Row>
          )}
          
          {/* Show table when not loading */}
          {!loading && (
            <HostsTable
              data={filteredHosts}
              isLoading={false}
              knownHostsMap={knownHostsMap}
              updatingHosts={updatingHosts}
              onToggleKnown={toggleKnownStatus}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default HostsMonitoring;



// import React, { useState, useEffect, useCallback } from 'react';
// import { Container, Row, Col, Spinner, Alert, Button, Form } from 'react-bootstrap';
// import { useAuth } from "react-oidc-context";
// import { useUser } from "../context/UserContext";
// import { hostMonitoringAPI } from '../utils/apiClient';
// import DataDropdown from '../components/DataDropdown';
// import HostsTable from '../components/HostsTable';
// import '../components/Dropdowns.css';

// function HostsMonitoring() {
//   useEffect(() => {
//     document.title = 'Host Monitoring - LostLogic';
//   }, []);
//   const auth = useAuth();
//   const { orgHash } = useUser();
  
//   // Data states
//   const [hosts, setHosts] = useState([]);
//   const [filteredHosts, setFilteredHosts] = useState([]);
//   const [knownHostsMap, setKnownHostsMap] = useState({});
  
//   // Loading and error states
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [isNotConfigured, setIsNotConfigured] = useState(false);
  
//   // Filter states
//   const [selectedDomain, setSelectedDomain] = useState('all');
//   const [selectedDate, setSelectedDate] = useState('');
//   const [availableDomains, setAvailableDomains] = useState([]);
//   const [availableDates, setAvailableDates] = useState([]);
  
//   // UI states
//   const [updatingHosts, setUpdatingHosts] = useState(new Set());
//   const [autoRefresh, setAutoRefresh] = useState(false);

//   // Check if we have a cached "not configured" state (valid for 24 hours)
//   const checkNotConfiguredCache = useCallback(() => {
//     if (!orgHash) return false;
    
//     const cacheKey = `hosts_monitoring_not_configured_${orgHash}`;
//     const cached = localStorage.getItem(cacheKey);
    
//     if (cached) {
//       const { timestamp } = JSON.parse(cached);
//       const now = Date.now();
//       const twentyFourHours = 24 * 60 * 60 * 1000;
      
//       if (now - timestamp < twentyFourHours) {
//         console.log('Hosts monitoring not configured (cached)');
//         return true;
//       } else {
//         localStorage.removeItem(cacheKey);
//       }
//     }
//     return false;
//   }, [orgHash]);

//   // Set "not configured" state in cache (24 hour expiry)
//   const setNotConfiguredCache = useCallback(() => {
//     if (!orgHash) return;
    
//     const cacheKey = `hosts_monitoring_not_configured_${orgHash}`;
//     localStorage.setItem(cacheKey, JSON.stringify({
//       timestamp: Date.now()
//     }));
//   }, [orgHash]);

//   /**
//    * Fetch available dates
//    */
//   const fetchAvailableDates = useCallback(async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) {
//       console.log('Skipping fetchAvailableDates - waiting for auth');
//       return [];
//     }

//     try {
//       console.log('Fetching available dates...');
//       const data = await hostMonitoringAPI.getDates(token, orgHash);
//       console.log('Available dates:', data);

//       setAvailableDates(data.dates || []);
      
//       if (data.most_recent && !selectedDate) {
//         setSelectedDate(data.most_recent);
//       }

//       return data.dates;
//     } catch (err) {
//       console.error('Error fetching dates:', err);
//       setError('Failed to load available dates');
//       return [];
//     }
//   }, [auth.user, orgHash, selectedDate]);

//   /**
//    * Fetch known hosts status
//    */
//   const fetchKnownHosts = useCallback(async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) {
//       console.log('Skipping fetchKnownHosts - waiting for auth');
//       return;
//     }

//     try {
//       console.log('Fetching known hosts status...');
//       const data = await hostMonitoringAPI.getKnownHosts(token, orgHash);
//       console.log('Known hosts:', data);
//       setKnownHostsMap(data.hosts || {});
//     } catch (err) {
//       console.error('Error fetching known hosts:', err);
//       setKnownHostsMap({});
//     }
//   }, [auth.user, orgHash]);

//   /**
//    * Fetch host monitoring data
//    */
//   const fetchHostData = useCallback(async (date) => {
//     if (!date) {
//       console.log('No date selected, skipping data fetch');
//       return;
//     }

//     const token = auth.user?.access_token;
//     if (!token || !orgHash) {
//       console.log('Skipping fetchHostData - waiting for auth');
//       return;
//     }

//     setLoading(true);
//     setError(null);

//     try {
//       console.log(`Fetching host data for date: ${date}`);
//       const result = await hostMonitoringAPI.getHostData(token, orgHash, date);
      
//       const hostData = result.data;
//       console.log(`Loaded ${hostData.length} hosts from S3`);

//       if (!hostData || hostData.length === 0) {
//         console.log('Empty host data received - marking as not configured');
//         setNotConfiguredCache();
//         setIsNotConfigured(true);
//         setHosts([]);
//         setLoading(false);
//         return;
//       }

//       // Map data to expected format
//       const mappedData = hostData.map(item => ({
//         host: item.hosts || item.host,
//         domain: item.domain || '',
//         discovered: item.discovered || ''
//       }));

//       setHosts(mappedData);

//       // Extract unique domains
//       const domains = [...new Set(mappedData.map(h => h.domain).filter(d => d))];
//       setAvailableDomains(domains.sort());

//       setLoading(false);
//     } catch (err) {
//       console.error('Error fetching host data:', err);
//       setError(err.message || 'Failed to load host data');
//       setHosts([]);
//       setLoading(false);
//     }
//   }, [auth.user, orgHash, setNotConfiguredCache]);

//   /**
//    * Toggle known status for a host
//    */
//   const toggleKnownStatus = async (host, domain, currentStatus) => {
//     const token = auth.user?.access_token;
//     if (!token) return;

//     // Optimistic update
//     setUpdatingHosts(prev => new Set(prev).add(host));
//     const newStatus = !currentStatus;
    
//     setKnownHostsMap(prev => ({
//       ...prev,
//       [host]: newStatus
//     }));

//     try {
//       console.log(`Toggling known status for ${host} to ${newStatus}`);
//       await hostMonitoringAPI.toggleKnown(token, orgHash, host, domain, newStatus);
//       console.log('Status updated successfully');
//     } catch (err) {
//       console.error('Error toggling known status:', err);
      
//       // Revert on error
//       setKnownHostsMap(prev => ({
//         ...prev,
//         [host]: currentStatus
//       }));
      
//       alert(`Failed to update known status: ${err.message}`);
//     } finally {
//       setUpdatingHosts(prev => {
//         const newSet = new Set(prev);
//         newSet.delete(host);
//         return newSet;
//       });
//     }
//   };

//   /**
//    * Manual refresh
//    */
//   const handleRefresh = () => {
//     if (selectedDate) {
//       fetchHostData(selectedDate);
//       fetchKnownHosts();
//     }
//   };

//   /**
//    * Initial data load
//    */
//   useEffect(() => {
//     const loadInitialData = async () => {
//       const token = auth.user?.access_token;
      
//       if (!token) {
//         setError('Authentication token not found');
//         setLoading(false);
//         return;
//       }

//       if (!orgHash) {
//         console.log('Waiting for orgHash...');
//         return;
//       }

//       if (checkNotConfiguredCache()) {
//         setIsNotConfigured(true);
//         setLoading(false);
//         return;
//       }

//       const dates = await fetchAvailableDates();
//       await fetchKnownHosts();
      
//       if (!dates || dates.length === 0) {
//         setNotConfiguredCache();
//         setIsNotConfigured(true);
//         setLoading(false);
//       }
//     };

//     loadInitialData();
//   }, [auth.user, orgHash, fetchAvailableDates, fetchKnownHosts, checkNotConfiguredCache, setNotConfiguredCache]);

//   /**
//    * Load host data when date changes
//    */
//   useEffect(() => {
//     if (selectedDate) {
//       fetchHostData(selectedDate);
//     }
//   }, [selectedDate, fetchHostData]);

//   /**
//    * Filter hosts by domain
//    */
//   useEffect(() => {
//     if (selectedDomain === 'all') {
//       setFilteredHosts(hosts);
//     } else {
//       setFilteredHosts(hosts.filter(h => h.domain === selectedDomain));
//     }
//   }, [hosts, selectedDomain]);

//   /**
//    * Auto-refresh (if enabled)
//    */
//   // useEffect(() => {
//   //   if (!autoRefresh || !selectedDate) return;
    
//   //   const interval = setInterval(() => {
//   //     fetchHostData(selectedDate);
//   //     fetchKnownHosts();
//   //   }, 30000); // 30 seconds
    
//   //   return () => clearInterval(interval);
//   // }, [autoRefresh, selectedDate, fetchHostData, fetchKnownHosts]);

//   // Not configured view
//   if (isNotConfigured) {
//     return (
//       <Container fluid className="mt-4">
//         <h2>Host Monitoring</h2>
//         <Alert variant="info" className="mt-4">
//           <Alert.Heading>
//             <i className="fas fa-info-circle me-2"></i>
//             Hosts Monitoring is not configured
//           </Alert.Heading>
//           <p className="mb-0">
//             This feature is not yet available for your organization. Please contact your administrator for more information.
//           </p>
//         </Alert>
//       </Container>
//     );
//   }

//   return (
//     <Container fluid className="mt-4">
//       {/* Header */}
//       <Row className="mb-3">
//         <Col>
//           <h2>Host Monitoring</h2>
//         </Col>
//         <Col xs="auto">
//           <Button 
//             variant="outline-primary" 
//             onClick={handleRefresh}
//             disabled={loading}
//           >
//             <i className="fas fa-sync-alt"></i> Refresh
//           </Button>
//           {' '}
//           <Button 
//             variant={autoRefresh ? "success" : "outline-secondary"}
//             onClick={() => setAutoRefresh(!autoRefresh)}
//             size="sm"
//           >
//             Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
//           </Button>
//         </Col>
//       </Row>

//       {/* Error Display */}
//       {error && (
//         <Alert variant="danger" dismissible onClose={() => setError(null)}>
//           {error}
//         </Alert>
//       )}

//       {/* Filters */}
//       <Row className="mb-3">
//         <Col xs={12} lg={10}>
//           <Form className="d-flex flex-wrap align-items-center">
//             <DataDropdown 
//               setSelectedProperty={(value) => {
//                 if (value === 'All Domains') {
//                   setSelectedDomain('all');
//                 } else {
//                   setSelectedDomain(value);
//                 }
//               }}
//               data={['All Domains', ...availableDomains]}
//               placeholder="Domain" 
//               inputID="domainSelector" 
//               inputName="domainProperties" 
//               className="m-1" 
//               zIndex="10"
//               value={selectedDomain === 'all' ? 'All Domains' : selectedDomain}
//             />
//             {selectedDomain !== 'all' && filteredHosts.length > 0 && (
//               <small className="text-muted ms-2">
//                 ({filteredHosts.length} hosts)
//               </small>
//             )}
//           </Form>
//         </Col>
//         <Col xs={12} lg={2} className="d-flex justify-content-lg-end align-items-center">
//           <DataDropdown 
//             setSelectedProperty={setSelectedDate}
//             data={availableDates}
//             placeholder={availableDates.length === 0 ? "No dates available" : "Date"}
//             inputID="dateSelector" 
//             inputName="dateProperties"
//             className="m-1"
//             zIndex="6"
//             value={selectedDate}
//           />
//         </Col>
//       </Row>

//       {/* Host Table */}
//       <Row>
//         <Col>
//           <h4 className="mb-3">
//             Discovered Hosts
//             {loading && <Spinner animation="border" size="sm" className="ms-2" />}
//           </h4>
          
//           <HostsTable
//             data={filteredHosts}
//             isLoading={loading}
//             knownHostsMap={knownHostsMap}
//             updatingHosts={updatingHosts}
//             onToggleKnown={toggleKnownStatus}
//           />
//         </Col>
//       </Row>
//     </Container>
//   );
// }

// export default HostsMonitoring;